import { AsyncPipe, Location } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { serverTimestamp, Timestamp } from '@angular/fire/firestore';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import {
  GradeLesson,
  TeacherNotes,
} from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { NotesType } from '../../../../models/lesson.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { UserModel } from '../../../../models/user.model';
import { SchoolGradeLessonService } from '../../../../services/grade-lesson.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as gradeLessonActions from '../../../../state/actions/grade-lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeLessonSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { RichTextEditorComponent } from '../../../components/quill-editor/rich-text-editor/rich-text-editor.component';
import { S3DocumentUploadComponent } from '../../../components/uploaders/s3-document-upload/s3-document-upload.component';

@Component({
  selector: 'app-create-notes',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    BtnBackComponent,
    BtnSubmitComponent,
    S3DocumentUploadComponent,
    RichTextEditorComponent,
  ],
  templateUrl: './create-notes.component.html',
  styleUrl: './create-notes.component.css',
})
export class CreateNotesComponent {
  @ViewChild('uploader') uploader: S3DocumentUploadComponent | undefined;

  location: Location = inject(Location);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  store: Store<AppState> = inject(Store<AppState>);
  private actions$: Actions = inject(Actions);
  gradeLessonService = inject(SchoolGradeLessonService);
  toastr: ToastrService = inject(ToastrService);
  pageForm: FormGroup;
  private dialog: MatDialog = inject(MatDialog);

  uuid = uuidv4();

  htmlData: string = '';

  groupId: string | null = this.route.snapshot.paramMap.get('id');
  facultyId: string | null = this.route.snapshot.paramMap.get('facultyId');
  lessonId: string | null = this.route.snapshot.paramMap.get('lessonId');
  academicYear: number | null = null;

  gradeLesson: GradeLesson | null = null;
  group: Group | undefined;
  grade: Grade | null | undefined;

  loadingTeacherNotes$: Observable<boolean> = of(false);
  myRoles$: Observable<any> = new Observable();

  user: UserModel | null | undefined = null;

  preSignedUrlEndpoint: string =
    environment.AWS_SCHOOL_GRADELESSONNOTES_SIGNEDURL;
  apiKey: string = environment.AWS_API_KEY;
  metadata: any = {};
  inputParams: any = {};
  cloudFrontDomain = environment.AWS_GROUPS_CLOUDFRONT;

  notesList = [NotesType.pdf, NotesType.text];
  membershipStatus = MembershipStatus;
  noteTypes = NotesType;

  exit$: Subject<void> = new Subject();

  private params$ = combineLatest({
    // Use .parent if the component is in a child router-outlet
    params: this.route.paramMap,
    // Use queryParams from the current route
    queryParams: this.route.queryParamMap,
  }).pipe(
    map(({ params, queryParams }) => {
      // If current params are empty, try to look at the parent
      let pMap = params;
      let currentRoute = this.route;

      // Walk up the tree until we find the groupId or hit the top
      while (currentRoute.parent && !pMap.has('id')) {
        currentRoute = currentRoute.parent;
        pMap = currentRoute.snapshot.paramMap;
      }

      return {
        groupId: pMap.get('id'),
        facultyId: pMap.get('facultyId'),
        lessonId: pMap.get('lessonId'),
        academicYear: queryParams.get('academicYear')
          ? Number(queryParams.get('academicYear'))
          : undefined,
      };
    }),
    takeUntil(this.exit$),
  );

  constructor() {
    this.route.paramMap.subscribe((params) => {
      this.groupId = params.get('id');
      this.facultyId = params.get('facultyId');
      this.lessonId = params.get('lessonId');
    });

    this.pageForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      type: new FormControl(NotesType.pdf),
    });

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );
  }

  ngOnInit(): void {
    this.uploader?.setMetadata(this.metadata);

    this.store
      .select(gradeLessonSelector.getGradeLessonById(this.lessonId ?? ''))
      .pipe(takeUntil(this.exit$))
      .subscribe((lesson) => {
        this.gradeLesson = lesson ?? null;
      });

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
        if (user == null) return;
        this.store
          .select(
            gradeSelector.getGradeByTeacherId({
              teacherId: this.user?.uid!,
              groupId: this.groupId!,
              academicYear: this.academicYear!,
            }),
          )
          .pipe(takeUntil(this.exit$))
          .subscribe((grade) => {
            this.grade = grade;
          });
      });

    this.store
      .select(groupSelector.getGroupById(this.groupId!))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
        this.metadata.academicYear = group?.academicYear;
      });

    this.loadingTeacherNotes$ = this.store.select(gradeLessonSelector.loading);

    this.actions$
      .pipe(ofType(gradeLessonActions.updateSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.toastr.success(
          'Teacher notes created successfully. Notes might take a while to refresh.',
          'Create Successful',
        );
        this.loadingTeacherNotes$ = of(false);
        this.location.back();
      });

    this.actions$
      .pipe(ofType(gradeLessonActions.updateFailure), takeUntil(this.exit$))
      .subscribe((action) => {
        this.onShowAlert(action.error as AppExceptionModel);
        this.loadingTeacherNotes$ = of(false);
      });
  }

  setMetadata() {
    this.metadata = {
      gradeLessonId: this.uuid,
      academicYear: this.gradeLesson?.academicYear,
      groupId: this.groupId,
      gradeId: this.gradeLesson?.gradeId!,
    };
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onContentChanged(event: any): void {
    this.htmlData = event;
  }

  onChangeType(event: any): void {
    const value = event.target.value;
    this.pageForm.patchValue({ type: value });
  }

  async create() {
    if (
      (this.pageForm.get('type')?.value == NotesType.pdf &&
        !this.uploader?.getIsAttached()) ||
      (this.pageForm.get('type')?.value == NotesType.text &&
        (this.htmlData == null || this.htmlData.trim() === ''))
    )
      return;

    this.loadingTeacherNotes$ = of(true);

    const uuid = uuidv4();

    this.setMetadata();

    const dynamicParams = {
      id: uuid,
      contentType: `application/${this.pageForm.get('type')?.value}`,
      ...this.metadata,
    };

    if (this.pageForm.get('type')?.value == NotesType.pdf) {
      this.uploader?.setInputParams(dynamicParams);
      this.uploader?.setMetadata(this.metadata);

      this.uploader
        ?.upload()
        ?.then((result) => {
          if (result?.successful && result.successful[0]?.response?.uploadURL) {
            // console.log(`File uploaded Name: ${result.successful[0].name}`);
            this.createDatabaseEntry(
              uuid,
              result.successful[0].name ?? uuid,
              result.successful[0]?.response?.uploadURL,
            );
          } else {
            this.loadingTeacherNotes$ = of(false);
            this.toastr.error(
              `Failed to upload document. ${result?.failed}`,
              'Document Upload Error',
            );
          }
        })
        .catch((error) => {
          this.loadingTeacherNotes$ = of(false);
          // console.log(`Error uploading document: ${error.toString()}`);
          this.onShowAlert(error as AppExceptionModel);
        });
    } else if (this.pageForm.get('type')?.value == NotesType.text) {
      this.onPublish();
    }
  }

  async createDatabaseEntry(id: string, name: string, url: string) {
    // console.log(
    //   `Creating Teacher Notes :: createDatabaseEntry: id: ${id}, Name: ${name} Url: ${JSON.stringify(
    //     url
    //   )}`
    // );

    const notes: TeacherNotes = {
      id: id,
      type: this.pageForm.get('type')?.value,
      name: this.pageForm.get('name')?.value,
      url: UtilsService.replaceDomain(url, this.cloudFrontDomain),
      created: Timestamp.now(),
      createdBy: this.user?.name,
      createdById: this.user?.uid,
      modified: Timestamp.now(),
    };

    let teacherNotes = [...(this.gradeLesson?.teacherNotes ?? []), notes];

    try {
      let data: GradeLesson = {
        ...this.gradeLesson,
        teacherNotes: teacherNotes,
      } as GradeLesson;

      this.store.dispatch(
        gradeLessonActions.update({
          groupId: this.groupId!,
          lessonId: this.gradeLesson!.id!,
          gradeLesson: data,
        }),
      );
    } catch (error) {
      this.onShowAlert(error as AppExceptionModel);
    }
    this.loadingTeacherNotes$ = of(false);
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title ?? 'Error Occurred',
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }

  async onPublish(): Promise<void> {
    this.loadingTeacherNotes$ = of(true);

    try {
      // call two promises and wait for them to complete
      const [response, lessonData] = await Promise.all([
        await this.gradeLessonService.getNotesUploadUrl({
          groupId: this.groupId,
          academicYear: this.gradeLesson?.academicYear,
          gradeId: this.gradeLesson?.gradeId,
          id: this.uuid,
          fileExtension: 'json',
          metadata: {
            groupId: this.groupId,
            id: this.uuid,
            academicYear: this.gradeLesson?.academicYear,
            gradeId: this.gradeLesson?.gradeId,
            uploadedBy: this.user?.uid,
          },
        }),
        this.htmlData,
      ]);

      // Call AWS SignedUrl to upload the lesson
      this.gradeLessonService
        .uploadNotes(response.url, lessonData)
        .then((_) => {
          // console.log(`Uploaded Lesson Notes Url: ${JSON.stringify(response)}`);

          let teacherNotes: TeacherNotes[] = [
            ...(this.gradeLesson?.teacherNotes ?? []),
            {
              id: this.uuid,
              type: this.pageForm.get('type')?.value,
              name: this.pageForm.get('name')?.value,
              url: `https://${environment['AWS_GROUPS_CLOUDFRONT']!}/${
                response.path
              }`,
              created: Timestamp.now(),
              createdBy: this.user?.name,
              createdById: this.user?.uid,
              modified: Timestamp.now(),
            },
          ];
          this.store.dispatch(
            gradeLessonActions.update({
              groupId: this.groupId!,
              lessonId: this.lessonId!,
              gradeLesson: {
                teacherNotes: teacherNotes,
                modified: serverTimestamp(),
              } as GradeLesson,
            }),
          );
        })
        .catch((error: any) => {
          this.toastr.error(
            `An error occurred while uploading lesson notes. Please try again later.`,
            'Error Publishing Notes',
          );
          this.loadingTeacherNotes$ = of(false);
        });
    } catch (error) {
      this.toastr.error(
        `An error occurred during notes upload. Please try again later.`,
        'Error Publishing Notes',
      );
      this.loadingTeacherNotes$ = of(false);
    }
  }
}
